/* eslint-disable no-bitwise */
import './style.scss';

import React, { useState } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { Button, Form, Input, Popup } from 'semantic-ui-react';

import FilesystemUtility, {
  checkFileAccesses,
  FILE_ACCESSES,
  isDirectory,
} from 'app/interfaces/FileSystemUtility';
import electron from 'app/native/node/electron';
import FSFileSystemUtility from 'app/adapters/FSFileSystemUtility';

type DirectorySelectorProps = {
  intl: IntlShape;
  label?: string | React.ReactNode;
  fileSystemUtility: FilesystemUtility;
  requiredDirectoryAccesses?: number;
  useDirectory: () => string | undefined;
  setSelectedDirectory: (directory: string) => void;
  placeholderKey: string;
  invalidDirectoryKey: string;
  setDirectoryDescriptionKey: string;
  clearDirectoryDescriptionKey: string;
};

function DirectorySelector({
  intl,
  useDirectory,
  setSelectedDirectory,
  placeholderKey,
  invalidDirectoryKey,
  setDirectoryDescriptionKey,
  clearDirectoryDescriptionKey,
  requiredDirectoryAccesses = FILE_ACCESSES.READ | FILE_ACCESSES.WRITE,
  label = undefined,
  fileSystemUtility = new FSFileSystemUtility(),
}: DirectorySelectorProps) {
  const selectedDirectory = useDirectory();
  const [isInvalidDirectory, setIsInvalidDirectory] = useState(false);
  const dialog = electron()?.remote.dialog;
  if (!dialog) return null;

  const openDirectorySelector = async () => {
    const { filePaths, canceled } = await dialog.showOpenDialog({ properties: ['openDirectory'] });
    if (canceled) return;

    const fileInfo = fileSystemUtility.getFileInfoSync(filePaths[0]);
    const isSelectedFileDirectory = isDirectory(fileInfo);
    const isMatchingAccessRights = checkFileAccesses(fileInfo, requiredDirectoryAccesses);
    if (!isSelectedFileDirectory || !isMatchingAccessRights) {
      setIsInvalidDirectory(true);
    } else {
      setIsInvalidDirectory(false);
      setSelectedDirectory(filePaths[0]);
    }
  };

  const onClearWatcher = () => {
    setSelectedDirectory(undefined);
  };

  let error;
  if (isInvalidDirectory) {
    error = intl.formatMessage({ id: invalidDirectoryKey });
  }

  return (
    <div className="directory-picker">
      <Form.Field
        className="directory-picker__input"
        error={error}
        readOnly
        placeholder={intl.formatMessage({ id: placeholderKey })}
        control={(props: React.ComponentProps<typeof Input>) => <Input label={label} {...props} />}
        value={selectedDirectory ?? ''}
      />
      <div className="directory-picker__button-group">
        <Popup
          content={<FormattedMessage id={setDirectoryDescriptionKey} />}
          trigger={
            <Button
              icon="folder open"
              error={error}
              onClick={openDirectorySelector}
              aria-label="pick-directory"
              data-testid="pick-directory"
            />
          }
        />
        <Popup
          content={<FormattedMessage id={clearDirectoryDescriptionKey} />}
          trigger={
            <Button
              color="red"
              icon="trash"
              aria-label="clear-directory"
              onClick={onClearWatcher}
            />
          }
        />
      </div>
    </div>
  );
}

export default injectIntl(DirectorySelector);
